import React, { useState, useLayoutEffect } from 'react';
import { DataGridPro, DataGridProProps, GridColDef, GridRenderCellParams, GridToolbar, GridInitialState, useGridApiRef } from '@mui/x-data-grid-pro';
import { useTheme, Fab, Stack, Box, IconButton, Typography, Grid, Modal, Button, Container, CircularProgress, Tooltip } from '@mui/material';
import { Add as AddIcon, Cached as CachedIcon, CloudUpload as CloudUploadIcon, Grading as GradingIcon } from '@mui/icons-material';
import { useAmaWorkflowContext } from '../../../../context/ama-workflow-provider';
import SnapshotsListRowTemplate from '../snapshots-list-row-template';
import { EditMode } from '../../../../data/enums';
import SnapshotlDataQualityReport from '../snapshot-data-quality-report';
import { useDynamicGridHeight } from '../../../../hooks/useDynamicGridHeight';
import { useSearchParams } from 'react-router-dom';
import { localStorageKeys, urlParams } from '../../../../data/constants';

const GRID_VERT_OFFSET = 300;

interface ISnapshotsListProps {
  mode: EditMode;
  setEditMode: React.Dispatch<React.SetStateAction<EditMode>>;
};

export default function SnapshotsList(props: ISnapshotsListProps) {
  
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [params, setParams] = useSearchParams();
  const { snapshots, setSelectedSnapshot, snapshotsLastUpdated, snapshotsLoading, snapshotsError, loadSnapshots, deleteSnapshot, clearSnapshotErrors } = useAmaWorkflowContext();
  const [snapshotForDelete, setSnapshotForDelete] = useState<ISnapshot | null>(null);
  const [snapshotForDQR, setSnapshotForDQR] = useState<ISnapshot | null>(null);
  const getDetailPanelContent = React.useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => <DetailPanelContent row={row} />, []);
  const getDetailPanelHeight = React.useCallback(() => 'auto', []);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { gridHeight } = useDynamicGridHeight({ gridItems: snapshots, offset: GRID_VERT_OFFSET });

  useLayoutEffect(() => {

    // Save grid state when view unmounts
    return () => {
      if (apiRef.current && apiRef.current.exportState) {
        console.log(`SnapshotsList.useLayoutEffect :: saving grid state to local storage`);
        localStorage.setItem(
          localStorageKeys.AMA_SNAPSHOTS_GRID_STATE, 
          JSON.stringify(apiRef.current.exportState()));
      }
    };
  }, []);

  useLayoutEffect(() => {

    const selectedId = params.get(urlParams.ID);
    if (!selectedId || !snapshots || !apiRef.current) return;

    // Working around DataGridPro bug related to scrollToIndexes
    setTimeout(() => {
      try {

        const [clientName, snapshotDate] = selectedId.split('-', 2);        
        const rowIndex = snapshots.findIndex(snapshot => snapshot.ClientId === clientName && snapshot.SnapshotDate === snapshotDate);
        if (rowIndex >= 0) apiRef.current?.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 

      } catch (error) {
        console.error(`SnapshotsList :: error scrolling to selected item: ${error}`);
      }
    }, 500);

  }, [snapshots]);

  const getInitialGridState = () : GridInitialState => {
    
    // Check for saved grid filters
    const gridStateStr = localStorage.getItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE);
    
    // Init initial grid state obj
    const gridState = gridStateStr ? JSON.parse(gridStateStr) : {};

    // Check querystring for selected row
    const selectedId = params.get(urlParams.ID);
    if (selectedId && snapshots) {

      try {

        const [clientName, snapshotDate] = selectedId.split('-', 2);        
        const rowIndex = snapshots.findIndex(snapshot => snapshot.ClientId === clientName && snapshot.SnapshotDate === snapshotDate);
        if (rowIndex >= 0) {
          const detailPanel = { 'expandedRowIds': [selectedId] };
          gridState.detailPanel = detailPanel;
        }
      } catch (error) {
        console.error(`SnapshotsList.getInitialGridState :: error getting selected item: ${error}`);
      }
    }

    // Check querystring for grid filter - TODO
    const qsFilterStr = params.get(urlParams.AMA_GRID_FILTER);   

    // Return initial grid state
    return gridState;
  };

  const handleEditButtonClick = (clientId: string, snapshotDate: string) => {
    setSelectedSnapshot(clientId, snapshotDate);   
    props.setEditMode(EditMode.Edit);
  };

  const handleAddButtonClick = () => {
    props.setEditMode(EditMode.Add);
  };

  const handleRefreshButtonClick = async () => {
    await loadSnapshots();
  };

  const handleModalOkButtonClick = async () => {
    
    if (!snapshotForDelete) return;
    
    const snapshot = snapshots?.find(item => item.ClientId === snapshotForDelete.ClientId && 
      item.SnapshotDate === snapshotForDelete.SnapshotDate);

    // Close modal imediately
    setSnapshotForDelete(null);

    // Delete snapshot
    if (snapshot) {
      await deleteSnapshot(snapshot.ClientId, snapshot.SnapshotDate);      
    }
  };

  const handleModalCancelButtonClick = () => {
    setSnapshotForDelete(null);
  };

  const handleDQModalCloseButtonClick = () => {
    setSnapshotForDQR(null);
  };  

  const handleErrorModalOkButtonClick = () => {
    clearSnapshotErrors();
  };

  const CustomFooter = () => {
    return (
      <Container sx={{ height:  40 }}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ height: '100%', color: theme.palette.text.secondary }}>
          <Grid item xs={4}>
            <Typography variant="body2" align="left">
              Records: {snapshots?.length}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography variant="body2" align="center" sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.secondary }}>
            <a href=""
              aria-label="Refresh Snapshot List"
              aria-describedby='snapshot_refresh_timestamp'
              title='Refresh Snapshot List'
              onClick={(e) => { e.preventDefault(); handleRefreshButtonClick();}}>
             <CachedIcon 
                sx={{ 
                  verticalAlign: 'middle', 
                  mr: 1, mb:.3, 
                  color: theme.palette.text.disabled,
                  animation: snapshotsLoading ? 'spin 1s linear infinite' : 'none', 
                }} />             
            </a>
            <span id="snapshot_refresh_timestamp"><span className='visually-hidden'>Last Refreshed: </span>{snapshotsLastUpdated}</span>              
            </Typography>
          </Grid>
          <Grid item xs={4} md={4}>
            &nbsp;
          </Grid>
        </Grid>
      </Container>
    );
  };

  const DetailPanelContent = ({ row }: { row: ISnapshot }) => {
    return (
      <SnapshotsListRowTemplate 
        snapshot={row}
        setIsDownloading={setIsDownloading}
        setSnapshotForDelete={setSnapshotForDelete}/>
    );
  };

  const RenderUploadColumn = (snapshot: ISnapshot) => {
    if (!snapshot) return <></>
    return (
      <Tooltip title="Upload files to Bronze.">
        <IconButton 
          aria-label="Upload files to Bronze."
          sx={{ color: theme.palette.grey[600]}}
          onClick={() => 
            { 
              handleEditButtonClick(snapshot.ClientId, snapshot.SnapshotDate);
            }}>
          <CloudUploadIcon />
        </IconButton>
      </Tooltip>
    )
  };

  const RenderDQReportColumn = (snapshot: ISnapshot) => {
    
    const clickHandler = async () => {
      setSnapshotForDQR(snapshot);
    };

    return (
      <Tooltip title="View Silver Data Quality Report and Logging.">
        <IconButton 
          aria-label="View Silver Data Quality Report and Logging."
          sx={{ color: theme.palette.grey[600]}}
          disabled={false}
          onClick={clickHandler}>
          <GradingIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const columns: Array<GridColDef> = [  
    { 
      field: 'ClientId', type: 'string', headerName: 'Client ID', 
      flex: .2, minWidth: 150,
      resizable: true, sortable: true
    }, 
    { 
      field: 'SnapshotDate', type: 'date', headerName: 'Snapshot Date', 
      flex: .2, minWidth: 150, 
      headerAlign: 'center', align: 'center',
      valueGetter: ({ value }) => value && value.length == 8 && new Date(value.substring(0,4) + '-' + value.substring(4, 6) + '-' + value.substring(6) + 'T00:00:00'),
      valueFormatter: ({ value }) => {  if (!value) return ''; else return value.getFullYear() + '-' + ('0' + (value.getMonth() + 1)).slice(-2) + '-' + ('0' + value.getDate()).slice(-2)  },
      resizable: true, sortable: true
    },
    { 
      field: 'Status', type: 'string', headerName: 'ETL Status', 
      flex: .4, minWidth: 200,
      headerAlign: 'left', align: 'left', 
      resizable: true, sortable: true
    },
    { 
      field: 'Upload', type: 'string', headerName: 'Upload Files', 
      minWidth: 125,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderUploadColumn(params.row as ISnapshot) } </>)
    },
    { 
      field: 'QualityReport', type: 'string', headerName: 'Data Quality', 
      minWidth: 125,
      headerAlign: 'center', align: 'center',
      resizable: true, sortable: false,
      renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderDQReportColumn(params.row as ISnapshot) } </>)
    }
  ];

  return (
    <Box sx={{ width: '100%', height: '100%' }}>

      {/* Loading overlay */}
      {(snapshotsLoading || isDownloading) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 999,
          }}>
          <CircularProgress />
        </div>
      )}

      {/* Grid */}
      {snapshots && 
            
        <DataGridPro
          columns={columns}
          rows={wrapSnapshots(snapshots)}
          rowThreshold={0}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          apiRef={apiRef}
          initialState={getInitialGridState()}
          // onStateChange={saveGridState}
          disableDensitySelector={true}
          disableColumnResize={false}          
          disableColumnSelector={true}                  
          slots={{ toolbar: GridToolbar, footer: () => (
            <Stack justifyContent="center" alignItems="center">
              <CustomFooter />
            </Stack>) 
          }}         
          slotProps={{
            toolbar: {
              showQuickFilter: true,            
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true }
          }}}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'
          }           
          sx={{
            height: gridHeight,
            minHeight: '300px',
            '& .MuiDataGrid-root': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme => theme.palette.background.paper,
              color: theme => theme.palette.text.primary,
              fontSize: '.9rem'
            },
            '& .MuiDataGrid-cell': {
              color: theme => theme.palette.text.primary,
              fontSize: '0.9rem'
            },
            '& .MuiDataGrid-row.Mui-even .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[100]
            },
            '& .MuiDataGrid-row:hover .MuiDataGrid-cell': {
              backgroundColor: theme => theme.palette.grey[300]
            }
          }} 
        />
      }

      {/* Add Snapshot button */}
      <Fab color="primary" aria-label="add" 
        sx={{ position: 'absolute', bottom: '10px', right: '65px' }}
        onClick={() => handleAddButtonClick()}>
        <AddIcon />
      </Fab>   

      {/* Delete confirmation modal */}
      <Modal
        open={Boolean(snapshotForDelete)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Delete
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            Are you sure you want to delete {snapshotForDelete?.ClientId}/{snapshotForDelete?.SnapshotDate}?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" sx={{mr: 2}} onClick={handleModalOkButtonClick}>Ok</Button>
            <Button variant="outlined" color="primary" onClick={handleModalCancelButtonClick}>Cancel</Button>
          </Box>
        </Box>
      </Modal>

      {/* Data Quality Report modal */}
      <Modal
        open={Boolean(snapshotForDQR)}
        aria-labelledby="dql-report"
        aria-describedby="dql-report">
        <>
          <SnapshotlDataQualityReport 
            snapshot={snapshotForDQR}
            closeModal={handleDQModalCloseButtonClick} />
        </>
      </Modal>

      {/* Error modal */}
      <Modal
        open={Boolean(snapshotsError)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={{
          position: 'absolute' as const,
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,}}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Error
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 4 }}>
            {snapshotsError}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={handleErrorModalOkButtonClick}>Ok</Button>
          </Box>
        </Box>
      </Modal>

    </Box>
  );
}

const wrapSnapshots = (snapshots: Array<ISnapshot>) => {  
  return snapshots.map((jsonObject) => {
    const newObj = { ...jsonObject, id: jsonObject.ClientId + '-' + jsonObject.SnapshotDate };
    return newObj;
  });
};


// const RenderSilverDownloadColumn = (snapshot: ISnapshot) => {
    
  //   const clickHandler = async () => {
  //     const prefix = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH}/silver/ama_${snapshot.ClientId}/snapshot=${snapshot.SnapshotDate}/csv/`;
  //     const files = await listObjectsAtPrefix(prefix);
  //     const key = files.pop()?.BronzePath;
  //     if (!key) return;
  //     setIsDownloading(true);
  //     await downloadObject(key);
  //     setIsDownloading(false);
  //   };

  //   if (snapshot.SilverPath) {
  //     return (
  //       <Tooltip title="Download Silver File (csv).">
  //         <IconButton 
  //           aria-label="Download Silver File (csv)."
  //           sx={{ color: theme.palette.grey[600]}}
  //           disabled={false}
  //           onClick={clickHandler}>
  //         <FileDownloadIcon />
  //       </IconButton>
  //     </Tooltip>);
  //   } else {
  //     return (        
  //       <IconButton 
  //         aria-label="Download Silver File (csv)."
  //         sx={{ color: theme.palette.grey[600]}}
  //         disabled={true}
  //         onClick={clickHandler}>
  //         <FileDownloadIcon />
  //       </IconButton>);      
  //   }
  // };

    // { 
    //   field: 'SilverPath', type: 'string', headerName: 'Silver Download', 
    //   minWidth: 125,
    //   headerAlign: 'center', align: 'center',
    //   resizable: true, sortable: false,
    //   renderCell: (params: GridRenderCellParams<any, Date>) => (<> { RenderSilverDownloadColumn(params.row as ISnapshot) } </>)
    // },

// const { downloadObject, listObjectsAtPrefix } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET! });

  // React.useEffect(() => {    
  //   if (!apiRef.current) return;  
  //     if (params.get('id') != null)
  //     {        
  //       scrollToRow(params.get('id') ?? '', true, 0);     
  //     }
  // }, [apiRef]);

  // useEffect(() => {
  //   const id = params.get(urlParams.ID);
  //   if (id) scrollToRow(id, true, 0);
  // }, [apiRef]);

  // const scrollToRow = ((client: string, clearLocation: boolean, attempt: number) => { // TODO: In the future, change this to not need settimeout.
  //   if (!apiRef.current.getRowIndexRelativeToVisibleRows)
  //   {    
  //     if (attempt < 20) setTimeout(function() { scrollToRow(client, clearLocation, attempt + 1); }, 250);
  //   } 
  //   else
  //   {
  //     const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(client);
  //     if (rowIndex >= 0) apiRef.current.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 
  //     if (clearLocation) clearIdParameter(); 
  //   }
  // });

  // const scrollToRow = (client: string, clearLocation: boolean, attempt: number) => { // TODO: In the future, change this to not need settimeout.
  //   // if we're using setTimeout, should be a clear timeout
  //   // if (!apiRef.current.getRowIndexRelativeToVisibleRows) {    
  //   if (!apiRef.current || !apiRef.current.getRowIndexRelativeToVisibleRows) {   
  //     if (attempt < 20) setTimeout(function() { scrollToRow(client, clearLocation, attempt + 1); }, 250);
  //   } else {
  //     const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(client);
  //     if (rowIndex >= 0) apiRef.current.scrollToIndexes({ rowIndex: rowIndex, colIndex: 0 }); 
  //     if (clearLocation) clearIdParameter(); 
  //   }
  // };

  // const initializeView = (viewId: string, storageKey: string) => {
  //   if (viewId) return {detailPanel: { expandedRowIds: [viewId]}}; 
  //   else {
  //     const stateParams = params.get(urlParams.AMA_GRID_FILTER);
  //     const stateParamsArray = stateParams ? JSON.parse(decodeURIComponent(stateParams)) : [];
  //     if (stateParamsArray.length > 0) return { filter: stateParamsArray };
  //     else {
  //       const stateFromLocalStorage = localStorage?.getItem(storageKey);
  //       if (stateFromLocalStorage) return JSON.parse(stateFromLocalStorage);
  //     }
  //     return {};
  //   }    
  // }; 

  // // const state: GridInitialState = initializeView(params.get('id') ?? '', 'snapshotGridState'); 
  // const state: GridInitialState = initializeView(
  //   params.get(urlParams.ID) ?? '', 
  //   localStorageKeys.AMA_SNAPSHOTS_GRID_STATE);  
  
  // const saveGridState = (() => {
  //   if (!apiRef.current) return;
  //   if (apiRef.current.exportState && localStorage) {
  //     const currentState = JSON.stringify(apiRef.current.exportState());
  //     localStorage.setItem(localStorageKeys.AMA_SNAPSHOTS_GRID_STATE, currentState);  
  //   }
  // });

  // const clearIdParameter = (() => {  
  //   params.delete(urlParams.ID);
  //   setParams(params);
  //   saveGridState();
  // });

  // const setUrlParameters =(() =>{
  //   const currentState = encodeURIComponent(JSON.stringify(apiRef?.current?.exportState().filter));
  //   setParams({[urlParams.AMA_GRID_FILTER]: currentState});
  // });


    // const RenderDeleteColumn = (snapshot: ISnapshot) => {
  //   if (!snapshot) return <></>
  //   return (
  //     <Tooltip title="Delete snapshot record (this will remove all files from Bronze and Silver).">
  //       <IconButton 
  //         aria-label="Delete snapshot record."
  //         sx={{ color: theme.palette.grey[600]}}
  //         onClick={() => 
  //           { 
  //             handleDeleteButtonClick(snapshot.ClientId, snapshot.SnapshotDate);
  //           }}>
  //         <DeleteIcon />
  //       </IconButton>
  //     </Tooltip>
  //   )
  // };


    // const handleDeleteButtonClick = async (clientId: string, snapshotDate: string) => {
  //   setSnapshotForDelete({ ClientId: clientId, SnapshotDate: snapshotDate, Status: '', IsCollectionComplete: false, Jobs: []});
  // };